import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { DashboardStats, ConversionStatusCounts } from "@/lib/types";
import { resolvePeriod, type Period } from "@/lib/period";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Gera serie diaria de `days` posicoes (mais antigo -> mais recente).
 * Aceita qualquer array de strings ISO e conta por data local.
 */
function buildDailySeries(timestamps: string[], startDate: Date, days: number): number[] {
  const keys = Array.from({ length: days }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return dateKey(d);
  });
  const counts = new Map<string, number>(keys.map((k) => [k, 0]));
  for (const ts of timestamps) {
    const key = dateKey(new Date(ts));
    if (counts.has(key)) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return keys.map((k) => counts.get(k) ?? 0);
}

/**
 * Agrega lista de status normalizando para as categorias canonicas do dashboard.
 *
 * Vocabulario real gravado pelo bot em conversion_attempts.status:
 *   - 'success'              -> categoria success
 *   - 'error_no_provider'    -> categoria error (detail: no_provider)
 *   - 'error_no_credential'  -> categoria error (detail: no_credential)
 *   - 'error_provider'       -> categoria error (detail: provider)
 *   - 'error_link_create'    -> categoria error (detail: link_create)
 *   - 'pending'              -> categoria pending (reservado, nao gerado atualmente)
 *   - 'skipped'              -> categoria skipped (reservado para futuros paths)
 *   - 'skipped_quota_exceeded' -> categoria skipped (gerado pelo bot quando cota esgotada)
 *
 * O bot NUNCA grava o literal 'error' ou 'skipped' - os skips sem auditoria
 * (disabled, source_not_allowed, etc.) simplesmente nao chegam a tabela.
 * Por isso o agregador trata qualquer prefixo 'error_' como error e qualquer
 * prefixo 'skipped_' como skipped (analogo ao tratamento de error_*).
 */
/** @internal Exportada apenas para testes. Nao usar fora deste modulo. */
export function aggregateStatusCounts(statuses: string[]): ConversionStatusCounts {
  const acc: ConversionStatusCounts = { success: 0, error: 0, skipped: 0, pending: 0, total: statuses.length };
  for (const s of statuses) {
    const norm = (s ?? "").toLowerCase().trim();
    if (norm === "success") acc.success++;
    else if (norm.startsWith("error_") || norm === "error") acc.error++;
    else if (norm.startsWith("skipped_") || norm === "skipped") acc.skipped++;
    else if (norm === "pending") acc.pending++;
  }
  return acc;
}

/** Delta percentual vs periodo anterior. Null quando previous === 0 (metrica nova). */
function calcDelta(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

async function fetchDashboardStats(period: Period): Promise<DashboardStats> {
  const { current, previous, days } = resolvePeriod(period);

  const curStart = current.start.toISOString();
  const curEnd = current.end.toISOString();
  const prevStart = previous.start.toISOString();
  const prevEnd = previous.end.toISOString();

  // clicksToday sempre e "hoje" independente do period selecionado
  const todayStart = new Date();
  const startOfToday = new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate()).toISOString();

  const [
    groupsRes,
    linksRes,
    clicksTodayRes,
    clicksCurrentRes,
    clickSeriesRes,
    // Status de conversoes no periodo atual (status + created_at para serie e agregacao)
    convCurrentRes,
    // Status de conversoes no periodo anterior (so status para contagem de delta)
    convPrevRes,
    clicksPrevRes,
  ] = await Promise.all([
    supabase
      .from("telegram_groups")
      .select("id", { count: "exact", head: true })
      .eq("status", "active"),

    supabase
      .from("links")
      .select("id", { count: "exact", head: true }),

    supabase
      .from("click_events")
      .select("id", { count: "exact", head: true })
      .gte("clicked_at", startOfToday),

    supabase
      .from("click_events")
      .select("id", { count: "exact", head: true })
      .gte("clicked_at", curStart)
      .lte("clicked_at", curEnd),

    supabase
      .from("click_events")
      .select("clicked_at")
      .gte("clicked_at", curStart)
      .lte("clicked_at", curEnd)
      .order("clicked_at", { ascending: true }),

    // Busca status + created_at para calcular serie e taxa no client
    supabase
      .from("conversion_attempts")
      .select("status, created_at")
      .gte("created_at", curStart)
      .lte("created_at", curEnd),

    supabase
      .from("conversion_attempts")
      .select("status")
      .gte("created_at", prevStart)
      .lte("created_at", prevEnd),

    supabase
      .from("click_events")
      .select("id", { count: "exact", head: true })
      .gte("clicked_at", prevStart)
      .lte("clicked_at", prevEnd),
  ]);

  if (groupsRes.error) throw groupsRes.error;
  if (linksRes.error) throw linksRes.error;
  if (clicksTodayRes.error) throw clicksTodayRes.error;
  if (clicksCurrentRes.error) throw clicksCurrentRes.error;
  if (clickSeriesRes.error) throw clickSeriesRes.error;
  if (convCurrentRes.error) throw convCurrentRes.error;
  if (convPrevRes.error) throw convPrevRes.error;
  if (clicksPrevRes.error) throw clicksPrevRes.error;

  // Agregar status atuais (normalizando case no client)
  const currentRows = (convCurrentRes.data ?? []) as Array<{ status: string; created_at: string }>;
  const allStatuses = currentRows.map((r) => r.status ?? "");
  const conversionStatusCounts = aggregateStatusCounts(allStatuses);

  // Serie diaria de conversoes com sucesso (filtro no client, normalizando case)
  const successTimestamps = currentRows
    .filter((r) => (r.status ?? "").toLowerCase().trim() === "success")
    .map((r) => r.created_at);
  const dailySuccessConversions = buildDailySeries(successTimestamps, current.start, days);

  // Serie diaria de cliques
  const clickTimestamps = (clickSeriesRes.data ?? []).map(
    (r) => (r as { clicked_at: string }).clicked_at,
  );
  const dailyClicks = buildDailySeries(clickTimestamps, current.start, days);

  // Deltas
  const prevStatuses = (convPrevRes.data ?? []).map((r) => (r as { status: string }).status ?? "");
  const prevStatusCounts = aggregateStatusCounts(prevStatuses);
  const clicksCurrent = clicksCurrentRes.count ?? 0;
  const clicksPrev = clicksPrevRes.count ?? 0;

  return {
    activeGroups: groupsRes.count ?? 0,
    totalLinks: linksRes.count ?? 0,
    clicksToday: clicksTodayRes.count ?? 0,
    clicksThisMonth: clicksCurrent,
    dailyClicks,
    successConversions: conversionStatusCounts.success,
    dailySuccessConversions,
    conversionStatusCounts,
    deltas: {
      successConversions: calcDelta(conversionStatusCounts.success, prevStatusCounts.success),
      clicks: calcDelta(clicksCurrent, clicksPrev),
    },
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDashboardStats(period: Period = "30d") {
  return useQuery<DashboardStats, Error>({
    queryKey: ["dashboard-stats", period],
    queryFn: () => fetchDashboardStats(period),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
